import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import {
  forwardRef,
  type HTMLAttributes,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import { cn } from '@/shared/utils/cn';
import { Icon } from '../icons/Icon';

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  /** Nombre del recuadro desplazable para lectores de pantalla (el recuadro recibe foco de teclado). */
  regionLabel: string;
  containerClassName?: string;
}

/** Tabla densa (14 px) dentro de un recuadro que se desplaza en horizontal sin romper la página. */
export const Table = forwardRef<HTMLTableElement, TableProps>(function Table(
  { regionLabel, containerClassName, className, ...rest },
  ref,
) {
  return (
    <div
      role="region"
      aria-label={regionLabel}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- una región con scroll debe ser alcanzable con teclado
      tabIndex={0}
      className={cn(
        'relative w-full overflow-x-auto rounded-lg border border-border-soft bg-card',
        containerClassName,
      )}
    >
      <table
        ref={ref}
        className={cn('w-full border-collapse text-left text-sm', className)}
        {...rest}
      />
    </div>
  );
});

export function TableCaption({ className, ...rest }: HTMLAttributes<HTMLTableCaptionElement>) {
  return (
    <caption className={cn('px-4 py-3 text-left text-sm text-fg-muted', className)} {...rest} />
  );
}

export function TableHeader({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn('border-b border-border-soft bg-muted', className)} {...rest} />;
}

export function TableBody({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn('[&_tr:last-child]:border-0', className)} {...rest} />;
}

export function TableRow({ className, ...rest }: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={cn(
        'border-b border-border-soft transition-colors duration-fast ease-standard hover:bg-muted/60',
        className,
      )}
      {...rest}
    />
  );
}

export interface TableHeadProps extends Omit<ThHTMLAttributes<HTMLTableCellElement>, 'align'> {
  align?: 'left' | 'right';
  /** Con `onSort` la cabecera es un botón; `sort` indica la dirección vigente (aria-sort). */
  sort?: 'asc' | 'desc' | 'none';
  onSort?: () => void;
}

const ARIA_SORT = { asc: 'ascending', desc: 'descending', none: 'none' } as const;

export function TableHead({
  className,
  align = 'left',
  sort,
  onSort,
  children,
  ...rest
}: TableHeadProps) {
  const sortIcon = sort === 'asc' ? ArrowUp : sort === 'desc' ? ArrowDown : ArrowUpDown;
  return (
    <th
      scope="col"
      aria-sort={onSort ? ARIA_SORT[sort ?? 'none'] : undefined}
      className={cn(
        'h-11 whitespace-nowrap px-4 text-xs font-semibold uppercase tracking-wider text-fg-muted',
        align === 'right' && 'text-right',
        className,
      )}
      {...rest}
    >
      {onSort ? (
        <button
          type="button"
          onClick={onSort}
          className={cn(
            'inline-flex h-11 items-center gap-1 uppercase tracking-wider hover:text-fg',
            align === 'right' && 'flex-row-reverse',
          )}
        >
          {children}
          <Icon
            icon={sortIcon}
            size={16}
            className={sort && sort !== 'none' ? 'text-primary' : 'opacity-60'}
          />
        </button>
      ) : (
        children
      )}
    </th>
  );
}

export interface TableCellProps extends Omit<TdHTMLAttributes<HTMLTableCellElement>, 'align'> {
  /** `numeric` alinea a la derecha con cifras tabulares (precios, porcentajes). */
  align?: 'left' | 'numeric';
}

export function TableCell({ className, align = 'left', ...rest }: TableCellProps) {
  return (
    <td
      className={cn(
        'px-4 py-3 align-middle text-fg',
        align === 'numeric' && 'num whitespace-nowrap text-right',
        className,
      )}
      {...rest}
    />
  );
}

/** Fila de mensaje que ocupa toda la tabla: cargando, vacía o con error. */
export function TableStateRow({
  colSpan,
  children,
}: {
  colSpan: number;
  children: React.ReactNode;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-10 text-center text-fg-muted">
        {children}
      </td>
    </tr>
  );
}
